// Standalone Skip-enquiry attachment upload.
//
// The React Query upload hook (useUploadSkipEnquiryAttachments) takes the
// enquiry enc_id at hook-call time, which is awkward on create forms where
// the enc_id only exists after the create mutation resolves. This helper
// sidesteps the hook-ordering problem: call it from a success handler once
// the enc_id is known.
//
// Mirrors the hook's mutationFn — compress (images only; PDFs pass through),
// build a multipart body under the `attachments` field, POST with the
// multipart header so axios generates the boundary.

import { apiClient } from './client';
import { endpoints } from './endpoints';
import { compressFilesForUpload } from '../utils/compressForUpload';

/**
 * Fire-and-forget upload of staged files to an existing Skip enquiry.
 * Never throws — failures are surfaced via the optional onError callback
 * so callers can navigate / close immediately without awaiting.
 */
export async function uploadEnquiryAttachmentsBackground(
  encId: string,
  files: File[],
  onError?: (err: unknown) => void,
): Promise<void> {
  if (!files.length) return;
  try {
    const compressed = await compressFilesForUpload(files);
    const formData = new FormData();
    for (const f of compressed) formData.append('attachments', f, f.name);
    await apiClient.post<{ attachments: unknown[] }, FormData>(
      endpoints.skipEnquiries.attachments(encId),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  } catch (err) {
    onError?.(err);
  }
}
